import { keyframes } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { VoiceDialogPhase } from "../voiceLaunch";

/** Hue-shift shimmer shared by the assistant FAB and the active send button. */
export const gradientShimmer = keyframes`
  0%   { filter: hue-rotate(0deg) saturate(1); }
  50%  { filter: hue-rotate(28deg) saturate(1.2); }
  100% { filter: hue-rotate(0deg) saturate(1); }
`;

/** Static fallback applied on hover when the user prefers reduced motion. */
export const REDUCED_MOTION_SHIMMER_FILTER = "hue-rotate(18deg) saturate(1.15)";

/**
 * Idle fill shared by the launcher mic and the composer's voice-dialog button.
 * Munis-pill blue (`userBubbleBg`) into `accentStrong`, so the hue-shift shimmer
 * stays in that family instead of the assistant's blue-to-purple gradient.
 */
export function voiceButtonIdleGradient(colors: {
  userBubbleBg: string;
  accentStrong: string;
}): string {
  return `linear-gradient(135deg, ${colors.userBubbleBg} 0%, ${colors.accentStrong} 100%)`;
}

/**
 * Solid status fills for an audio dialog. Green means speak, blue means the
 * assistant is talking, amber means wait. All three are theme kind tokens.
 */
export function voiceDialogPhaseColor(
  colors: { kindGreen: string; kindBlue: string; kindAmber: string },
  phase: "listening" | "speaking" | "thinking",
): string {
  if (phase === "listening") {
    return colors.kindGreen;
  }
  if (phase === "speaking") {
    return colors.kindBlue;
  }
  return colors.kindAmber;
}

/**
 * Fills for the floating voice cluster. Listening and speaking match the
 * screenshot hexes that already live in the theme. Thinking uses the existing
 * amber kind token; the screenshot's darker amber is not in the palette.
 */
export function voiceClusterPhaseColors(theme: Theme): {
  listening: string;
  speaking: string;
  thinking: string;
  speakingHover: string;
  darken: string;
} {
  const assist = theme.app.aiAssist;
  return {
    listening: theme.app.dialog.listToggleSuccessBackground,
    speaking: assist.accentStrong,
    thinking: assist.kindAmber,
    speakingHover: theme.palette.mode === "dark" ? assist.gradientStart : assist.userBubbleBg,
    darken: theme.palette.common.black,
  };
}

export function voiceClusterPhaseFill(
  colors: { listening: string; speaking: string; thinking: string },
  phase: Exclude<VoiceDialogPhase, "idle">,
): string {
  if (phase === "listening") {
    return colors.listening;
  }
  if (phase === "speaking") {
    return colors.speaking;
  }
  return colors.thinking;
}

/** Darken a phase fill on hover without introducing a new palette color. */
export function voiceClusterPhaseHover(
  colors: {
    listening: string;
    speaking: string;
    thinking: string;
    speakingHover: string;
    darken: string;
  },
  phase: Exclude<VoiceDialogPhase, "idle">,
): string {
  if (phase === "speaking") {
    return colors.speakingHover;
  }
  const fill = voiceClusterPhaseFill(colors, phase);
  return `color-mix(in srgb, ${fill} 85%, ${colors.darken})`;
}

function voiceClusterWash(fill: string, percent: number): string {
  return `color-mix(in srgb, ${fill} ${percent}%, transparent)`;
}

/** Soft colored glow under the pause/play button. */
export function voiceClusterStatusShadow(fill: string, mode: "light" | "dark"): string {
  if (mode === "dark") {
    return `0 6px 18px ${voiceClusterWash(fill, 45)}`;
  }
  return `0 6px 16px ${voiceClusterWash(fill, 35)}`;
}

/** Translucent ring that the listening/speaking pulse expands. */
export function voiceClusterRing(fill: string, mode: "light" | "dark"): string {
  return voiceClusterWash(fill, mode === "dark" ? 30 : 32);
}

/** Expanding halo behind the listening and speaking buttons. */
export const voiceStatusRing = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.6); opacity: 0; }
`;

/** Slow scale pulse behind the thinking button. */
export const voiceStatusBreathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.35; }
  50% { transform: scale(1.25); opacity: 0.08; }
`;

/** Status pill slides out from the button toward the left. */
export const voiceHintIn = keyframes`
  from { transform: translateX(100%); opacity: 0; pointer-events: none; }
  to { transform: translateX(0); opacity: 1; pointer-events: auto; }
`;

/** Status pill slides back under the button. */
export const voiceHintOut = keyframes`
  from { transform: translateX(0); opacity: 1; pointer-events: auto; }
  to { transform: translateX(100%); opacity: 0; pointer-events: none; }
`;

/** Same hold-and-hide without a horizontal slide. */
export const voiceHintFade = keyframes`
  0%, 91% { opacity: 1; pointer-events: auto; }
  100% { opacity: 0; pointer-events: none; }
`;

/** Equalizer bars on the composer speaking glyph. */
export const voiceSpeakingBar = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
`;

/** Sequential bounce for the composer thinking dots. */
export const voiceThinkingDot = keyframes`
  0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

/** Press, focus ring, and tap highlight shared by the voice-cluster buttons. */
export function voiceClusterMotionSx(focusColor: string) {
  const outline = {
    outline: `2px solid ${focusColor}`,
    outlineOffset: "3px",
  };
  return {
    WebkitTapHighlightColor: "transparent",
    transition:
      "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
    "&:active": { transform: "scale(0.94)" },
    "&:focus-visible": outline,
    "&.Mui-focusVisible": outline,
  };
}

/** Last style in a cluster button so it wins over the phase animations. */
export function voiceClusterReducedMotionSx() {
  return {
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:active": { transform: "none" },
      "&::before, &::after": { animation: "none", opacity: 0 },
    },
  };
}

/** Gentle opacity pulse for the mic button while voice input is listening. */
export const voicePulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.55; }
  100% { opacity: 1; }
`;

/** Scale/opacity pulse for the assistant avatar while its reply streams. */
export const assistantThinkingPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.22); opacity: 0.55; }
`;

/** Flowing gradient behind the assistant avatar while its reply streams. */
export const assistantAvatarGradient = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/**
 * Pulsing accent glow (halo) around the assistant avatar while its reply
 * streams. Stays visible regardless of the icon's own opacity, so the
 * "thinking" state reads clearly.
 *
 * The halo colors are intentionally brand-fixed: they are the assistant
 * gradient endpoints (blue #2563EB → purple #7C3AED, identical in light and
 * dark), so the halo matches the gradient that flows behind the avatar. Kept as
 * literals here because `@emotion` keyframes are defined at module scope and
 * cannot read `theme`; they read well on both the light and dark card surfaces.
 */
export const assistantAvatarGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.0); }
  50%      { box-shadow: 0 0 10px 3px rgba(124, 58, 237, 0.65); }
`;
